"use client";

import React from "react";
import axios from "axios";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  Text,
} from "@chakra-ui/react";
import { supabase } from "../../supabaseClient";

interface ConnectSMSNumberDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ConnectSMSFormData {
  twilio_account_sid: string;
  twilio_auth_token: string;
  phone_number: string;
}

export function ConnectSMSNumberDialog({
  isOpen,
  onClose,
  onSuccess,
}: ConnectSMSNumberDialogProps) {
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [formData, setFormData] = React.useState<ConnectSMSFormData>({
    twilio_account_sid: "",
    twilio_auth_token: "",
    phone_number: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleClose = () => {
    setFormData({
      twilio_account_sid: "",
      twilio_auth_token: "",
      phone_number: "",
    });
    onClose();
  };

  const handleSubmit = async () => {
    if (
      !formData.twilio_account_sid ||
      !formData.twilio_auth_token ||
      !formData.phone_number
    ) {
      toast({
        title: "Required fields missing",
        description: "Please fill in all required fields",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      await axios.post(
        `${process.env.REACT_APP_SERVER_URL || ""}/api/sms/numbers/connect`,
        {
          phone_number: formData.phone_number,
          twilio_account_sid: formData.twilio_account_sid,
          twilio_auth_token: formData.twilio_auth_token,
        },
        {
          headers: {
            Authorization: `Bearer ${session?.access_token}`,
          },
        }
      );

      toast({
        title: "SMS number connected",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error("Error connecting SMS number:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : error?.response?.data?.message || "An unexpected error occurred";
      toast({
        title: "Error connecting SMS number",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent bg="white">
        <ModalHeader color="gray.800">Connect Your SMS Number</ModalHeader>
        <ModalCloseButton color="gray.800" />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel color="gray.800">Twilio Account SID</FormLabel>
              <Input
                name="twilio_account_sid"
                value={formData.twilio_account_sid}
                onChange={handleChange}
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                bg="white"
                color="gray.800"
                borderColor="gray.200"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color="gray.800">Twilio Auth Token</FormLabel>
              <Input
                name="twilio_auth_token"
                value={formData.twilio_auth_token}
                onChange={handleChange}
                type="password"
                placeholder="Your Twilio Auth Token"
                bg="white"
                color="gray.800"
                borderColor="gray.200"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel color="gray.800">Phone Number</FormLabel>
              <Input
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                placeholder="+1XXXXXXXXXX"
                bg="white"
                color="gray.800"
                borderColor="gray.200"
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button
            colorScheme="white"
            textColor="gray.800"
            variant="ghost"
            mr={3}
            onClick={handleClose}
            isDisabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            colorScheme="primary"
            bg="primary.800"
            _hover={{ bg: "primary.900" }}
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText="Connecting..."
          >
            Connect
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
